"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative inline-flex items-center px-3 py-2 text-[14px] font-medium tracking-[0.01em] text-foreground hover:bg-transparent focus:bg-transparent group" style={{
            color: "var(--muted-foreground)"
          }}>
                <span className="relative font-default">
                  {item.label}
                  <span className="pointer-events-none absolute -bottom-1 left-0 h-px w-0 transition-all duration-[220ms] ease-out group-hover:w-full" style={{
                backgroundColor: "var(--primary)"
              }} aria-hidden="true" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 px-2">
        <div className="h-8 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-2/3 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-1/2 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 px-2">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onNavigate} className="font-default text-[1.5rem] leading-snug tracking-tight py-3 border-b border-border text-foreground hover:text-primary transition-colors duration-200">
          {item.label}
        </Link>)}
    </nav>;
}
export default function NavigationHub() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1795" id="navigation-hub" className={`sticky top-0 z-50 w-full transition-all duration-[220ms] ease-out bg-background text-foreground ${scrolled ? "bg-background/70 backdrop-blur-lg" : "bg-background"}`}>
      <div className="mx-auto flex h-[72px] w-full max-w-[1280px] items-center justify-between" style={{
      paddingLeft: "clamp(20px, 4vw, 64px)",
      paddingRight: "clamp(20px, 4vw, 64px)"
    }}>
        {}
        <div className="flex items-center md:w-1/4">
          <Link to="/" aria-label="Catalyst Growth Lab home" className="inline-flex items-center">
            <WvcLogo className="h-7 w-auto" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex md:w-2/4 md:justify-center">
          <MenuProvider menu_id="31">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center justify-end gap-2 md:w-1/4 md:gap-4">
          <Link to="/ContactPage" className="hidden md:inline-flex items-center text-[14px] font-medium tracking-[0.005em] text-foreground hover:text-primary transition-colors duration-200">
            <span className="relative group font-default">
              Client Login
              <span className="pointer-events-none absolute -bottom-1 left-0 h-px w-0 transition-all duration-[220ms] ease-out group-hover:w-full" style={{
              backgroundColor: "var(--primary)"
            }} aria-hidden="true" />
            </span>
          </Link>

          <Button asChild className="hidden md:inline-flex rounded-lg px-7 py-3 h-auto text-[14px] font-medium tracking-[0.005em] bg-primary text-primary-foreground hover:opacity-90">
            <Link to="/ContactPage">Let's Talk Strategy</Link>
          </Button>

          {}
          <Button asChild size="sm" className="md:hidden rounded-lg px-4 h-9 text-[13px] font-medium bg-primary text-primary-foreground">
            <Link to="/ContactPage">Strategy</Link>
          </Button>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" className="md:hidden text-foreground hover:bg-muted">
                <Menu className="size-5" strokeWidth={1.5} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background w-full sm:max-w-sm p-0">
              <SheetHeader className="px-6 pt-6 pb-2">
                <div className="flex items-center justify-between">
                  <SheetTitle className="font-mono text-[0.75rem] tracking-[0.18em] uppercase text-muted-foreground font-medium">
                    § 00 — Navigation
                  </SheetTitle>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="h-px w-16 bg-border" />
                  <span className="inline-block h-1 w-1 rounded-lg" style={{
                  backgroundColor: "var(--primary)"
                }} aria-hidden="true" />
                </div>
              </SheetHeader>

              <div className="px-4 py-4 flex-1 overflow-y-auto">
                <MenuProvider menu_id="31">
                  <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                </MenuProvider>
              </div>

              <div className="px-6 pb-8 pt-4 border-t border-border flex flex-col gap-3">
                <Link to="/ContactPage" onClick={() => setMobileOpen(false)} className="text-[14px] font-medium text-foreground hover:text-primary transition-colors font-default">
                  Client Login
                </Link>
                <Button asChild className="rounded-lg w-full h-11 text-[14px] font-medium bg-primary text-primary-foreground">
                  <Link to="/ContactPage" onClick={() => setMobileOpen(false)}>
                    Let's Talk Strategy
                  </Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {}
      {scrolled && <div className="relative w-full">
          <div className="mx-auto flex h-px w-full max-w-[1280px] items-center">
            <div className="h-px flex-1 bg-border" />
            <span className="ml-0 -mr-0 h-1 w-1 rounded-lg" style={{
          backgroundColor: "var(--primary)"
        }} aria-hidden="true" />
          </div>
        </div>}
    </section>;
}